import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useIdentity } from '@/features/permissions/AbilityProvider'
import { crm2Api, useCrm2Meta, useCrm2Write, type Pipeline } from './api'
import { FormGroup, MainAccountSelect, Readiness, SizeSelect, Suggestions } from './accountFields'
import { Field, fieldClass, fromDateInput, PersonSelect, PHONE_HINT, PHONE_PLACEHOLDER, ROLE_LABEL, todayPlus } from './shared'

/**
 * A new deal: the company (matched to an existing account by domain or name), its first contact, and where
 * the deal starts. Grouped by the part of the account page each field fills, with a checklist of what the
 * page will show, so a new deal arrives with an account worth opening.
 */
export function NewDealDialog({ pipeline: initial, onClose, onCreated, account }: { pipeline: Pipeline; onClose: () => void; onCreated: (id: string) => void; account?: { id: string; name: string } }) {
  const meta = useCrm2Meta().data
  const me = useIdentity()
  const [pipeline, setPipeline] = useState<Pipeline>(initial)
  const stages = meta?.pipelines.find((p) => p.key === pipeline)?.stages ?? []
  const [stage, setStage] = useState(initial === 'marketing' ? 'new' : (meta?.pipelines.find((p) => p.key === initial)?.stages[0]?.key ?? ''))
  const [company, setCompany] = useState(account?.name ?? '')
  const [domain, setDomain] = useState('')
  const [industry, setIndustry] = useState('')
  const [size, setSize] = useState('')
  const [parentId, setParentId] = useState('')
  const [contact, setContact] = useState({ name: '', title: '', email: '', phone: '', role: 'sponsor' })
  const [ownerId, setOwnerId] = useState<string | null>(me.userId)
  const [nextStep, setNextStep] = useState('')
  const [nextStepAt, setNextStepAt] = useState(() => todayPlus(2))
  const [checkInAt, setCheckInAt] = useState(() => todayPlus(30))
  const [value, setValue] = useState('')
  const [source, setSource] = useState('')
  const [products, setProducts] = useState<string[]>([])
  const [force, setForce] = useState(false)
  const [lookup, setLookup] = useState({ name: '', domain: '' })

  const dupes = useQuery({
    queryKey: ['crm2', 'dupes', lookup.name, lookup.domain],
    queryFn: () => crm2Api.duplicates(lookup.name, lookup.domain),
    enabled: !account && (lookup.name.trim().length >= 3 || lookup.domain.includes('.')),
    staleTime: 30_000,
  })
  const create = useCrm2Write(crm2Api.createDeal, (d) => `${d.accountName} added to ${d.stageLabel}`)

  const handOverOnly = pipeline === 'sales' || pipeline === 'post_sales'
  const nurture = stage === 'nurture'
  const needsNext = !nurture && (pipeline === 'marketing' || pipeline === 'sales')
  const hasValue = pipeline !== 'marketing' && pipeline !== 'podcast'
  const hasProducts = pipeline !== 'partnership' && pipeline !== 'podcast'

  const submit = (accountId?: string) => {
    create.mutate(
      {
        pipeline,
        stage,
        ...(accountId
          ? { accountId }
          : { account: { name: company.trim(), domain: domain.trim() || contact.email.trim() || null, industry: industry.trim() || null, size: size || null, parentId: parentId || null } }),
        contacts: contact.name.trim() ? [{ name: contact.name.trim(), title: contact.title.trim() || null, email: contact.email.trim() || null, phone: contact.phone.trim() || null, role: contact.role }] : [],
        ownerId,
        source: source.trim() || null,
        products,
        value: Number(value) || 0,
        nextStep: needsNext || nextStep.trim() ? nextStep.trim() || null : null,
        nextStepAt: needsNext || nextStep.trim() ? fromDateInput(nextStepAt) : null,
        checkInAt: nurture ? fromDateInput(checkInAt) : null,
        force: handOverOnly ? force : undefined,
      },
      {
        onSuccess: (d) => {
          onClose()
          onCreated(d.id)
        },
      },
    )
  }

  const nextOk = nurture ? Boolean(checkInAt) : Boolean(nextStep.trim() && nextStepAt)
  const checklist = [
    { label: 'Company name', done: Boolean(company.trim()), required: true },
    ...(account
      ? []
      : [
          { label: 'Domain', done: Boolean(domain.trim() || contact.email.includes('@')) },
          { label: 'Industry', done: Boolean(industry.trim()) },
          { label: 'Size', done: Boolean(size) },
        ]),
    { label: 'Account owner', done: Boolean(ownerId) },
    { label: 'Lead source', done: Boolean(source.trim()) },
    { label: 'A contact with title and email', done: Boolean(contact.name.trim() && contact.title.trim() && contact.email.trim()) },
    { label: 'Contact number (for WhatsApp)', done: Boolean(contact.phone.trim()) },
    { label: 'Stage', done: Boolean(stage), required: true },
    { label: nurture ? 'Check-in date' : 'Next step and date', done: nextOk, required: needsNext || nurture },
    ...(hasProducts ? [{ label: 'Products of interest', done: products.length > 0 }] : []),
    ...(hasValue ? [{ label: 'Value', done: Number(value) > 0 }] : []),
  ]

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>New deal{account ? ` for ${account.name}` : ''}</DialogTitle>
          <DialogDescription>New leads start in Marketing. A Sales deal comes from a hand-over at Demo Done.</DialogDescription>
        </DialogHeader>
        <Suggestions />
        <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_15rem]">
          <div className="grid gap-4">
            {account ? null : (
              <FormGroup title="Company" fills="the account header and properties">
                <Field label="Company name">
                  <Input value={company} onChange={(e) => setCompany(e.target.value)} onBlur={() => setLookup({ name: company, domain })} placeholder="Acme Staffing" />
                </Field>
                <Field label="Website or domain">
                  <Input value={domain} onChange={(e) => setDomain(e.target.value)} onBlur={() => setLookup({ name: company, domain })} placeholder="acme.com" />
                </Field>
                {dupes.data?.length ? (
                  <div className="rounded-lg border border-warning bg-warning-soft p-2 text-sm sm:col-span-2">
                    <p className="mb-1 font-medium text-warning">Already in TapCRMv2? Add the deal to the existing account instead:</p>
                    <div className="flex flex-wrap gap-2">
                      {dupes.data.map((a) => (
                        <Button key={a.id} size="sm" variant="outline" disabled={create.isPending || !stage} onClick={() => submit(a.id)}>
                          Use {a.name}
                          {a.domain ? ` (${a.domain})` : ''}
                        </Button>
                      ))}
                    </div>
                  </div>
                ) : null}
                <Field label="Industry">
                  <Input list="crm2-industries" value={industry} onChange={(e) => setIndustry(e.target.value)} placeholder="BPO / Contact center" />
                </Field>
                <Field label="Size">
                  <SizeSelect value={size} onChange={setSize} />
                </Field>
                <Field label="Main account" hint="For a region or department of a bigger company.">
                  <MainAccountSelect value={parentId} onChange={setParentId} />
                </Field>
              </FormGroup>
            )}

            <FormGroup title="Main contact" fills="Contacts, with their own page">
              <Field label="Name">
                <Input value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} />
              </Field>
              <Field label="Title">
                <Input value={contact.title} onChange={(e) => setContact({ ...contact, title: e.target.value })} placeholder="Head of Talent" />
              </Field>
              <Field label="Email">
                <Input type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} onBlur={() => !account && !domain && setLookup({ name: company, domain: contact.email })} />
              </Field>
              <Field label="Contact number" hint={PHONE_HINT}>
                <Input type="tel" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} placeholder={PHONE_PLACEHOLDER} />
              </Field>
              <Field label="Role on the deal">
                <select className={fieldClass} value={contact.role} onChange={(e) => setContact({ ...contact, role: e.target.value })}>
                  {(meta?.contactRoles ?? []).map((r) => (
                    <option key={r} value={r}>
                      {ROLE_LABEL[r] ?? r}
                    </option>
                  ))}
                </select>
              </Field>
            </FormGroup>

            <FormGroup title="The deal" fills="Deals, highlights and the pipeline">
              <Field label="Pipeline">
                <select
                  className={fieldClass}
                  value={pipeline}
                  onChange={(e) => {
                    const p = e.target.value as Pipeline
                    setPipeline(p)
                    setStage(meta?.pipelines.find((x) => x.key === p)?.stages[p === 'marketing' ? 1 : 0]?.key ?? '')
                  }}
                >
                  {meta?.pipelines.map((p) => (
                    <option key={p.key} value={p.key}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Stage" hint={stages.find((s) => s.key === stage)?.rule}>
                <select className={fieldClass} value={stage} onChange={(e) => setStage(e.target.value)}>
                  {stages.map((s) => (
                    <option key={s.key} value={s.key}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Owner" hint={account ? undefined : 'Also becomes the account owner.'}>
                <PersonSelect label="Owner" value={ownerId} onChange={setOwnerId} />
              </Field>
              <Field label="Lead source">
                <Input list="crm2-sources" value={source} onChange={(e) => setSource(e.target.value)} placeholder="Demo request form" />
              </Field>
              {nurture ? (
                <Field label="Check in on" hint="Nurture needs a date to come back to it.">
                  <Input type="date" value={checkInAt} onChange={(e) => setCheckInAt(e.target.value)} />
                </Field>
              ) : (
                <>
                  <Field label={needsNext ? 'Next step (required)' : 'Next step'}>
                    <Input value={nextStep} onChange={(e) => setNextStep(e.target.value)} placeholder="Intro call" />
                  </Field>
                  <Field label="By">
                    <Input type="date" value={nextStepAt} onChange={(e) => setNextStepAt(e.target.value)} />
                  </Field>
                </>
              )}
              {hasValue ? (
                <Field label="Value (USD)">
                  <Input type="number" min={0} value={value} onChange={(e) => setValue(e.target.value)} />
                </Field>
              ) : null}
              {hasProducts ? (
                <fieldset className="grid gap-1 text-sm sm:col-span-2">
                  <legend className="mb-1 text-xs font-medium text-muted-foreground">Products of interest</legend>
                  <div className="flex flex-wrap gap-1.5">
                    {meta?.products.map((p) => {
                      const on = products.includes(p.key)
                      return (
                        <button key={p.key} type="button" aria-pressed={on} onClick={() => setProducts(on ? products.filter((x) => x !== p.key) : [...products, p.key])} className={on ? 'rounded-full border border-brand bg-accent px-2.5 py-0.5 text-xs font-medium' : 'rounded-full border border-border px-2.5 py-0.5 text-xs text-muted-foreground hover:bg-accent'}>
                          {p.label}
                        </button>
                      )
                    })}
                  </div>
                </fieldset>
              ) : null}
              {handOverOnly ? (
                <label className="flex items-center gap-2 text-sm sm:col-span-2">
                  <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} />
                  Create anyway (an existing client being entered by hand)
                </label>
              ) : null}
            </FormGroup>
          </div>
          <Readiness items={checklist} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => submit(account?.id)} disabled={create.isPending || !company.trim() || !stage || (handOverOnly && !force)}>
            {create.isPending ? 'Adding…' : 'Add deal'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
